import { useCallback } from 'react';

import { Apollo } from 'src/gql/generated';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

export function useCreateOrUpdateOrg() {
  const dispatch = useNotificationDispatch();
  const [createOrgMutation, { loading: creatingOrg }] = Apollo.useCreateOrgMutation();
  const [updateOrgMutation, { loading: updatingOrg }] = Apollo.useUpdateOrgMutation();
  const createOrg = useCallback(
    async (options: {
      props: {
        newMemberEmails: string[];
        orgName: string;
        isPreOnboardingCompleted?: boolean;
        size?: Apollo.OrgSize;
        type?: Apollo.OrgType;
        website?: string;
      };
      dispatchSnack?: boolean;
    }) => {
      const {
        dispatchSnack = true,
        props: { isPreOnboardingCompleted, newMemberEmails, orgName, size, type, website },
      } = options;
      const res = await createOrgMutation({
        variables: {
          input: {
            newMemberEmails,
            props: { isPreOnboardingCompleted, name: orgName, size, type, website },
          },
        },
      });
      if (!res.error && dispatchSnack)
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Org successfully created.',
            variant: 'success',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });

      return res;
    },
    [createOrgMutation, dispatch]
  );
  const updateOrg = useCallback(
    async (
      orgId: UUID,
      options: {
        newMemberEmails: string[];
        props: {
          isPreOnboardingCompleted?: boolean;
          name?: string;
          orgDefaultRole?: Apollo.OrgRole;
          signingReminderDays?: number | null;
          size?: Apollo.OrgSize;
          type?: Apollo.OrgType;
          website?: string;
        };
        dispatchSnack?: boolean;
      }
    ) => {
      const { dispatchSnack = true, newMemberEmails, props } = options;
      const res = await updateOrgMutation({
        variables: {
          input: {
            newMemberEmails,
            orgId,
            props,
          },
        },
      });
      if (!res.error && dispatchSnack)
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Org successfully updated.',
            variant: 'success',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });

      return res;
    },
    [dispatch, updateOrgMutation]
  );
  const updateOrgName = useCallback(
    async (orgId: UUID, orgName: string, newMemberEmails: string[] = []) => {
      return updateOrg(orgId, { newMemberEmails, props: { name: orgName } });
    },
    [updateOrg]
  );

  return {
    createOrg,
    loading: creatingOrg || updatingOrg,
    updateOrg,
    updateOrgName,
  };
}
